import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaClipboardList } from "react-icons/fa";
import moment from "moment";
import dayjs from "dayjs";
import styles from "../Create.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import {
  CustomButton,
  CustomDatePicker,
  CustomTextInput,
  CustomDropdown,
} from "../../../../components";
import PopupModal from "../../../../components/PopupModal/PopupModal";

const admissionTypeOptions = [
  { label: "VI-X", value: "VI-X" },
  { label: "XI-XII", value: "XI-XII" },
];

const AdmissionCycleConfigCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const client_id = clientData?.client_id || "";
  const school_id = clientData?.school_id || "";
  const academic_year_id = clientData?.academic_year_id || "";
  const academic_year = clientData?.academic_year || "";
  const staff_id = clientData?.master_id || "";

  // Edit mode detection
  const editId = location.state?.id || null;
  const isEditMode = !!editId;

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    admissionType: "",
    admissionName: "",
    admissionStartDate: null,
    admissionEndDate: null,
    screeningStartDate: null,
    screeningEndDate: null,
    interviewStartDate: null,
    interviewEndDate: null,
    completionDate: null,
  });

  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  // Fetch data for edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchAdmissionCycleData();
    }
  }, [editId]);

  const fetchAdmissionCycleData = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post(
        "/admission/viewAdmissionCycle",
        { id: Number(editId) },
      );

      if (response?.data?.status) {
        const data = response.data.data;
        setFormData({
          admissionType: data.admission_type || "",
          admissionName: data.name || "",
          admissionStartDate: data.start_date ? dayjs(data.start_date) : null,
          admissionEndDate: data.end_date ? dayjs(data.end_date) : null,
          screeningStartDate: data.screening_start_date
            ? dayjs(data.screening_start_date)
            : null,
          screeningEndDate: data.screening_end_date
            ? dayjs(data.screening_end_date)
            : null,
          interviewStartDate: data.interview_start_date
            ? dayjs(data.interview_start_date)
            : null,
          interviewEndDate: data.interview_end_date
            ? dayjs(data.interview_end_date)
            : null,
          completionDate: data.completion_date
            ? dayjs(data.completion_date)
            : null,
        });
      }
    } catch (error) {
      console.error("Error fetching admission cycle:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          error?.response?.data?.message ||
          translations.labels?.fetchFailed ||
          "Failed to fetch details. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };
  // Format date to YYYY-MM-DD
  const formatDate = (date) => {
    if (!date) return null;
    return dayjs(date).format("YYYY-MM-DD");
  };

  // Handle text input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle dropdown change
  const handleDropdownChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle date change with cascading resets
  const handleDateChange = (name, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // Admission Start → reset Admission End & all below if invalid
      if (
        name === "admissionStartDate" &&
        prev.admissionEndDate &&
        dayjs(value).isAfter(dayjs(prev.admissionEndDate))
      ) {
        updated.admissionEndDate = null;
        updated.screeningStartDate = null;
        updated.screeningEndDate = null;
        updated.interviewStartDate = null;
        updated.interviewEndDate = null;
        updated.completionDate = null;
      }

      // Admission End → reset Screening Start & all below if invalid
      // Admission End → always reset all next stages
      if (name === "admissionEndDate") {
        updated.screeningStartDate = null;
        updated.screeningEndDate = null;
        updated.interviewStartDate = null;
        updated.interviewEndDate = null;
        updated.completionDate = null;
      }

      // Screening Start → reset Screening End & all below if invalid
      if (
        name === "screeningStartDate" &&
        prev.screeningEndDate &&
        dayjs(value).isAfter(dayjs(prev.screeningEndDate))
      ) {
        updated.screeningEndDate = null;
        updated.interviewStartDate = null;
        updated.interviewEndDate = null;
        updated.completionDate = null;
      }

      // Screening End → reset Interview Start & all below if invalid
      if (
        name === "screeningEndDate" &&
        prev.interviewStartDate &&
        dayjs(value).isBefore(dayjs(prev.interviewStartDate))
      ) {
        updated.interviewStartDate = null;
        updated.interviewEndDate = null;
        updated.completionDate = null;
      }

      // Interview Start → reset Interview End & Completion if invalid
      if (
        name === "interviewStartDate" &&
        prev.interviewEndDate &&
        dayjs(value).isAfter(dayjs(prev.interviewEndDate))
      ) {
        updated.interviewEndDate = null;
        updated.completionDate = null;
      }

      // Interview End → reset Completion Date if invalid
      if (
        name === "interviewEndDate" &&
        prev.completionDate &&
        dayjs(value).isAfter(dayjs(prev.completionDate))
      ) {
        updated.completionDate = null;
      }

      return updated;
    });

    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!formData.admissionType) {
      newErrors.admissionType =
        translations.labels?.admissionTypeRequired ||
        "Admission Type is required";
    }
    if (!formData.admissionName?.trim()) {
      newErrors.admissionName =
        translations.labels?.admissionNameRequired ||
        "Admission Name is required";
    }
    if (!formData.admissionStartDate) {
      newErrors.admissionStartDate =
        translations.labels?.admissionStartDateRequired ||
        "Admission Start Date is required";
    }
    if (!formData.admissionEndDate) {
      newErrors.admissionEndDate =
        translations.labels?.admissionEndDateRequired ||
        "Admission End Date is required";
    }
    if (!formData.screeningStartDate) {
      newErrors.screeningStartDate =
        translations.labels?.screeningStartDateRequired ||
        "Screening Start Date is required";
    }
    if (!formData.screeningEndDate) {
      newErrors.screeningEndDate =
        translations.labels?.screeningEndDateRequired ||
        "Screening End Date is required";
    }
    if (!formData.interviewStartDate) {
      newErrors.interviewStartDate =
        translations.labels?.interviewStartDateRequired ||
        "Interview Start Date is required";
    }
    if (!formData.interviewEndDate) {
      newErrors.interviewEndDate =
        translations.labels?.interviewEndDateRequired ||
        "Interview End Date is required";
    }
    if (!formData.completionDate) {
      newErrors.completionDate =
        translations.labels?.completionDateRequired ||
        "Completion Date is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit (Create or Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      let response;

      if (isEditMode) {
        // UPDATE - PUT method
        const payload = {
          id: Number(editId),
          academic_year_id: Number(academic_year_id),
          name: formData.admissionName.trim(),
          admission_type:
            formData.admissionType?.value || formData.admissionType || null,
          start_date: formatDate(formData.admissionStartDate),
          end_date: formatDate(formData.admissionEndDate),
          screening_start_date: formatDate(formData.screeningStartDate),
          screening_end_date: formatDate(formData.screeningEndDate),
          interview_start_date: formatDate(formData.interviewStartDate),
          interview_end_date: formatDate(formData.interviewEndDate),
          completion_date: formatDate(formData.completionDate),
          status: 1,
          updated_by: Number(staff_id),
        };

        response = await AxiosInstance.put(
          "/admission/updateAdmissionCycle",
          payload,
        );
      } else {
        // CREATE - POST method
        const payload = {
          client_id: Number(client_id),
          school_id: Number(school_id),
          academic_year_id: Number(academic_year_id),
          name: formData.admissionName.trim(),
          admission_type: formData.admissionType?.value || null,
          start_date: formatDate(formData.admissionStartDate),
          end_date: formatDate(formData.admissionEndDate),
          screening_start_date: formatDate(formData.screeningStartDate),
          screening_end_date: formatDate(formData.screeningEndDate),
          interview_start_date: formatDate(formData.interviewStartDate),
          interview_end_date: formatDate(formData.interviewEndDate),
          completion_date: formatDate(formData.completionDate),
          status: 1,
          created_by: Number(staff_id),
        };

        response = await AxiosInstance.post(
          "/admission/createAdmissionCycle",
          payload,
        );
      }

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            response.data?.message ||
            (isEditMode
              ? translations.labels?.updateSuccess || "Updated successfully!"
              : translations.labels?.createSuccess || "Created successfully!"),
          redirectPath: "/user/Admission-Cycle-Config",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response.data?.message ||
            translations.labels?.saveFailed ||
            "Failed to save. Please try again.",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error saving admission cycle:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          error?.response?.data?.message ||
          translations.labels?.saveFailed ||
          "Failed to save. Please try again.",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle modal ok
  const handleSuccessOk = () => {
    setModalState((prev) => ({ ...prev, open: false }));
    if (modalState.type === "success") {
      navigate("/user/Admission-Cycle-Config");
    }
  };

  // Handle modal close (click outside — stay on page)
  const handleModalClose = () => {
    setModalState((prev) => ({ ...prev, open: false }));
    navigate("/user/Admission-Cycle-Config");
  };

  // Handle cancel
  const handleCancel = () => {
    navigate("/user/Admission-Cycle-Config");
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          {translations.labels?.loading || "Loading..."}
        </div>
      </div>
    );
  }

  return (
    <>
      <PopupModal
        open={modalState.open}
        type={modalState.type}
        title={
          modalState.type === "success"
            ? "Success"
            : modalState.type || "Failed"
        }
        message={modalState.message}
        onOk={handleSuccessOk}
        onClose={handleModalClose}
      />

      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaClipboardList />
          </div>
          <h1 className={styles.headerTitle}>
            {translations.labels?.admissionCycleConfig ||
              "Admission Cycle Config"}
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="row">
            {/* Academic Year (Read-only) */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.academicYear || "Acedemic Year"}
                name="academicYear"
                type="text"
                value={academic_year}
                disabled
                readOnly
                required
              />
            </div>

            {/* Admission Type Dropdown */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.admissionType || "Admission Type"}
                name="admissionType"
                value={formData.admissionType}
                onChange={(value) =>
                  handleDropdownChange("admissionType", value)
                }
                options={admissionTypeOptions}
                placeholder={translations.labels?.selectType || "Select Type"}
                required
                error={errors.admissionType}
                disabled={isEditMode}
              />
            </div>

            {/* Admission Name */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.admissionName || "Admission Name"}
                name="admissionName"
                type="text"
                value={formData.admissionName}
                onChange={handleInputChange}
                placeholder={translations.labels?.enterHere || "Enter here.."}
                required
                error={errors.admissionName}
              />
            </div>

            {/* Admission Start Date */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={
                  translations.labels?.admissionStartDate ||
                  "Admission Start Date"
                }
                name="admissionStartDate"
                value={
                  formData.admissionStartDate
                    ? dayjs(formData.admissionStartDate)
                    : null
                }
                onChange={(date) =>
                  handleDateChange("admissionStartDate", date)
                }
                placeholder={
                  translations.labels?.startDatePlaceholder ||
                  "Select start date"
                }
                required
                error={errors.admissionStartDate}
              />
            </div>

            {/* Admission End Date */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={
                  translations.labels?.admissionEndDate || "Admission End Date"
                }
                name="admissionEndDate"
                value={
                  formData.admissionEndDate
                    ? dayjs(formData.admissionEndDate)
                    : null
                }
                onChange={(date) => handleDateChange("admissionEndDate", date)}
                placeholder={
                  translations.labels?.endDatePlaceholder || "Select end date"
                }
                required
                error={errors.admissionEndDate}
                minDate={
                  formData.admissionStartDate
                    ? dayjs(formData.admissionStartDate)
                    : undefined
                }
              />
            </div>
          </div>

          <div className="row">
            {/* Screening Start Date */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={
                  translations.labels?.screeningStartDate ||
                  "Screening Start Date"
                }
                name="screeningStartDate"
                value={
                  formData.screeningStartDate
                    ? dayjs(formData.screeningStartDate)
                    : null
                }
                onChange={(date) =>
                  handleDateChange("screeningStartDate", date)
                }
                placeholder={
                  translations.labels?.startDatePlaceholder ||
                  "Select start date"
                }
                required
                error={errors.screeningStartDate}
                minDate={
                  formData.admissionEndDate
                    ? dayjs(formData.admissionEndDate)
                    : undefined
                }
              />
            </div>

            {/* Screening End Date */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={
                  translations.labels?.screeningEndDate || "Screening End Date"
                }
                name="screeningEndDate"
                value={
                  formData.screeningEndDate
                    ? dayjs(formData.screeningEndDate)
                    : null
                }
                onChange={(date) => handleDateChange("screeningEndDate", date)}
                placeholder={
                  translations.labels?.endDatePlaceholder || "Select end date"
                }
                required
                error={errors.screeningEndDate}
                minDate={
                  formData.screeningStartDate
                    ? dayjs(formData.screeningStartDate)
                    : undefined
                }
              />
            </div>
          </div>

          <div className="row">
            {/* Interview Start Date */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={
                  translations.labels?.interviewStartDate ||
                  "Interview Start Date"
                }
                name="interviewStartDate"
                value={
                  formData.interviewStartDate
                    ? dayjs(formData.interviewStartDate)
                    : null
                }
                onChange={(date) =>
                  handleDateChange("interviewStartDate", date)
                }
                placeholder={
                  translations.labels?.startDatePlaceholder ||
                  "Select start date"
                }
                required
                error={errors.interviewStartDate}
                minDate={
                  formData.screeningEndDate
                    ? dayjs(formData.screeningEndDate)
                    : undefined
                }
              />
            </div>

            {/* Interview End Date */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={
                  translations.labels?.interviewEndDate || "Interview End Date"
                }
                name="interviewEndDate"
                value={
                  formData.interviewEndDate
                    ? dayjs(formData.interviewEndDate)
                    : null
                }
                onChange={(date) => handleDateChange("interviewEndDate", date)}
                placeholder={
                  translations.labels?.endDatePlaceholder || "Select end date"
                }
                required
                error={errors.interviewEndDate}
                minDate={
                  formData.interviewStartDate
                    ? dayjs(formData.interviewStartDate)
                    : undefined
                }
              />
            </div>
          </div>

          <div className="row">
            {/* Completion Date */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={translations.labels?.completionDate || "Completion Date"}
                name="completionDate"
                value={
                  formData.completionDate
                    ? dayjs(formData.completionDate)
                    : null
                }
                onChange={(date) => handleDateChange("completionDate", date)}
                placeholder={
                  translations.labels?.startDatePlaceholder || "Select date"
                }
                required
                error={errors.completionDate}
                minDate={
                  formData.interviewEndDate
                    ? dayjs(formData.interviewEndDate)
                    : undefined
                }
              />
            </div>
          </div>

          {/* Buttons */}
          <div className={styles.buttonContainer}>
            <CustomButton
              type="submit"
              variant="contained"
              className={styles.submitButton}
              fullWidth={false}
              loading={submitting}
              loadingText={
                isEditMode
                  ? translations.labels?.updating || "Updating..."
                  : translations.labels?.submitting || "Submitting..."
              }
            >
              {isEditMode
                ? translations.labels?.updateButton || "UPDATE"
                : translations.labels?.save || "SUBMIT"}
            </CustomButton>

            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              {translations.labels?.cancel || "CANCEL"}
            </CustomButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default AdmissionCycleConfigCreate;
